/**
 * Google Analytics for Firebase.
 *
 * The native SDK (added in android/app/build.gradle, configured by
 * android/app/google-services.json) collects first_open, session_start and
 * user_engagement by itself. This module adds what only the web layer knows:
 * a screen_view per route, custom events, and the signed-in user id.
 *
 * Everything is a no-op off-device — see src/plugins/FirebaseAnalytics.ts.
 */

import { Capacitor } from '@capacitor/core';
import type { RouteLocationNormalized } from 'vue-router';
import { FirebaseAnalytics } from '@/plugins/FirebaseAnalytics';
import type { UserProfile } from '@/types/api';

const enabled = Capacitor.isNativePlatform();

/** Firebase rejects event/property names that are not [A-Za-z][A-Za-z0-9_]{0,39}. */
function sanitizeName(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9_]/g, '_').replace(/^[^A-Za-z]+/, '');
  return cleaned.slice(0, 40) || 'unnamed';
}

export function isFirebaseAnalyticsEnabled(): boolean {
  return enabled;
}

export function trackFirebaseScreenView(to: RouteLocationNormalized): void {
  if (!enabled) {
    return;
  }

  const routeName = typeof to.name === 'string' && to.name !== '' ? to.name : to.path;

  void FirebaseAnalytics.setScreen({
    screenName: sanitizeName(routeName),
    screenClass: 'MainActivity',
  }).catch(() => {
    // Analytics must never break navigation.
  });
}

export function trackFirebaseEvent(name: string, params?: Record<string, unknown>): void {
  if (!enabled) {
    return;
  }

  void FirebaseAnalytics.logEvent({ name: sanitizeName(name), params }).catch(() => {
    // Analytics must never break the calling flow.
  });
}

export function syncFirebaseAnalyticsUser(profile: UserProfile | null): void {
  if (!enabled) {
    return;
  }

  void FirebaseAnalytics.setUserId({ userId: profile?.id ?? null }).catch(() => {});
  void FirebaseAnalytics.setUserProperty({
    key: 'auth_provider',
    value: profile?.provider ?? null,
  }).catch(() => {});
}
